import BackendService from 'BackendService';
import { useCallback, type Dispatch, type SetStateAction } from 'react';
import type { ContentItem } from '../../models/ContentManagementInterface';
import type { AdminFundUpdateEntry } from '../../models/FundUpdateEntry';
import type { FundUpdatesAdminState } from './fundUpdatesAdmin.types';
import { todayDateString } from './fundUpdatesAdminUtils';

type SetAdminState = Dispatch<SetStateAction<FundUpdatesAdminState>>;

/** Fills in the fields a freshly created draft may come back without. */
function toDraftEntry(
  created: AdminFundUpdateEntry,
  fallbackDate: string,
  content: AdminFundUpdateEntry['content'],
  updateCard: AdminFundUpdateEntry['updateCard'],
): AdminFundUpdateEntry {
  return {
    ...created,
    updateDate: created.updateDate || fallbackDate,
    status: created.status || 'draft',
    publishedAt: created.publishedAt ?? null,
    isLegacy: created.isLegacy ?? false,
    hasPendingDraft: created.hasPendingDraft ?? false,
    content,
    updateCard: updateCard ?? null,
  };
}

function setDuplicating(setState: SetAdminState, sourceId: string, duplicating: boolean) {
  setState((prev) => {
    const duplicatingIds = new Set(prev.duplicatingIds);
    if (duplicating) duplicatingIds.add(sourceId);
    else duplicatingIds.delete(sourceId);
    return { ...prev, duplicatingIds };
  });
}

/** Creates new drafts — blank ones, or a copy of a published update's content. */
export function useFundUpdateDraftCreation(
  fundOpportunityId: string,
  newDraftDate: string,
  setState: SetAdminState,
) {
  const handleCreateDraft = useCallback(async () => {
    const draftDate = newDraftDate;
    setState((prev) => ({ ...prev, creatingDraft: true, error: null }));
    try {
      const entry = await new BackendService().createFundUpdate(fundOpportunityId, draftDate);
      const fullEntry = toDraftEntry(entry, draftDate, entry.content ?? [], entry.updateCard);
      setState((prev) => ({
        ...prev,
        creatingDraft: false,
        drafts: [...prev.drafts, fullEntry],
        newDraftDate: todayDateString(),
      }));
      return fullEntry;
    } catch (err) {
      console.error('[FundUpdatesAdminPanel] createDraft failed:', err);
      setState((prev) => ({
        ...prev,
        creatingDraft: false,
        error: 'Unable to create draft. Please try again.',
      }));
      return null;
    }
  }, [fundOpportunityId, newDraftDate, setState]);

  const handleDuplicateUpdate = useCallback(
    async (source: AdminFundUpdateEntry) => {
      const sourceId = source.externalId;
      setDuplicating(setState, sourceId, true);
      setState((prev) => ({ ...prev, error: null }));
      try {
        const backendService = new BackendService();
        const created = await backendService.createFundUpdate(fundOpportunityId, todayDateString());
        await backendService.uploadContentManagementUpdate('FundUpdate', created.externalId, {
          name: 'Fund update',
          relatedObject: 'FundUpdate',
          relatedObjectId: created.externalId,
          content: source.content as unknown as ContentItem[],
          updateCard: source.updateCard ?? undefined,
        });
        const fullEntry = toDraftEntry(
          created,
          todayDateString(),
          source.content,
          source.updateCard,
        );
        setDuplicating(setState, sourceId, false);
        setState((prev) => ({ ...prev, drafts: [...prev.drafts, fullEntry] }));
        return fullEntry;
      } catch (err) {
        console.error('[FundUpdatesAdminPanel] duplicateUpdate failed:', err);
        setDuplicating(setState, sourceId, false);
        setState((prev) => ({ ...prev, error: 'Unable to duplicate update. Please try again.' }));
        return null;
      }
    },
    [fundOpportunityId, setState],
  );

  return { handleCreateDraft, handleDuplicateUpdate };
}
